import { differenceInDays, set } from 'date-fns'

import { InternalPackage, PackageTier } from '../../../plans/types/Plans'
import {
  AddedOrganizationPackage,
  InternalOrganization,
  OrganizationBillingContact,
  OrganizationCreate,
  OrganizationPackage,
  OrganizationSave,
  OrganizationStatus,
  OrganizationTextField,
  organizationTextFields,
  OrganizationUpdate,
  SentOrganizationPackage,
} from '../../types/Organizations'

export const organizationLanguages = ['en', 'ja', 'zh'] as const

export const billingTypes = ['Email', 'Post']
export const invoiceCurrencies = ['EUR', 'USD']
export const invoiceFrequencies = ['Quarterly', 'Yearly']
export const paymentTerms = ['14 days', '30 days']

/** The options of a select, plus a stored value that isn't one of them (staging has `email` next to `Email`), so it is shown and kept. */
export const withStoredOption = (options: string[], stored?: string | null) => (stored && !options.includes(stored) ? [...options, stored] : options)

const subscriptionTiers = [PackageTier.Indie, PackageTier.Basic, PackageTier.Pro, PackageTier.Enterprise]

/** Legacy's status, except that a subscription package wins over a trial one (legacy let the last package decide). */
export const getOrganizationStatus = (organization: Pick<InternalOrganization, 'packages'>): OrganizationStatus => {
  if (!organization.packages.length) return OrganizationStatus.Inactive
  if (organization.packages.some(({ tier }) => subscriptionTiers.includes(tier))) return OrganizationStatus.Subscription
  if (organization.packages.some(({ tier }) => tier === PackageTier.Trial)) return OrganizationStatus.Trial
  return OrganizationStatus.NotAvailable
}

export const organizationStatusLabelKeys: Record<OrganizationStatus, string> = {
  [OrganizationStatus.Subscription]: 'internal-csm:organizations_status_subscription',
  [OrganizationStatus.Trial]: 'internal-csm:organizations_status_trial',
  [OrganizationStatus.NotAvailable]: 'internal-csm:organizations_status_not_available',
  [OrganizationStatus.Inactive]: 'internal-csm:organizations_status_inactive',
}

export const organizationStatusFilters = ['all', OrganizationStatus.Subscription, OrganizationStatus.Trial, OrganizationStatus.Inactive] as const
export type OrganizationStatusFilter = (typeof organizationStatusFilters)[number]

export const toOrganizationStatusFilter = (value: string | null): OrganizationStatusFilter =>
  organizationStatusFilters.find((filter) => filter === value) ?? OrganizationStatus.Subscription

/** The earliest expiration of the organization's packages; `undefined` when none expires. An expired package still counts. */
export const getFirstExpiration = (organization: Pick<InternalOrganization, 'packages'>) =>
  organization.packages.reduce<number | undefined>(
    (first, { expiration }) => (expiration > 0 && (first === undefined || expiration < first) ? expiration : first),
    undefined
  )

/** Whole days left, rounded towards zero like legacy's `moment.diff(…, 'days')`. */
export const getDaysUntil = (timestamp: number, now: number) => differenceInDays(timestamp, now)

export const isExpiringSoon = (timestamp: number | undefined, now: number) => {
  if (timestamp === undefined) return false
  const days = getDaysUntil(timestamp, now)
  return days >= 0 && days <= 30
}

export const hasUnlimitedGameSlots = (organization: Pick<InternalOrganization, 'packages'>) =>
  organization.packages.some(({ roles }) => (roles ?? []).includes('game_slots_unlimited'))

export const hasRole = (planPackage: Pick<OrganizationPackage, 'roles'>, role?: string) => !!role && (planPackage.roles ?? []).includes(role)

export type OrganizationFilters = {
  search: string
  status: OrganizationStatusFilter
  role?: string
  tier?: PackageTier
}

export const filterOrganizations = (organizations: InternalOrganization[], { search, status, role, tier }: OrganizationFilters) => {
  const term = search.trim().toLowerCase()
  return organizations.filter(
    (organization) =>
      (status === 'all' || getOrganizationStatus(organization) === status) &&
      (tier === undefined || organization.packages.some((planPackage) => planPackage.tier === tier)) &&
      (!term || (organization.name ?? '').toLowerCase().includes(term) || organization.packages.some(({ name }) => name.toLowerCase().includes(term))) &&
      (!role || organization.packages.some((planPackage) => hasRole(planPackage, role)))
  )
}

export const editableTextFields = [
  'vatNumber',
  'billingType',
  'invoiceCurrency',
  'invoiceFrequency',
  'paymentTerms',
  'billingAddress1',
  'billingAddress2',
  'billingZipCode',
  'billingCity',
  'billingCountry',
  'address1',
  'address2',
  'zipCode',
  'city',
  'country',
  'contactName',
  'contactEmail',
  'contactPhone',
] as const

export type EditableTextField = (typeof editableTextFields)[number]

export type FormPackage = OrganizationPackage | AddedOrganizationPackage

export type OrganizationFormValues = Record<EditableTextField, string> & {
  name: string
  defaultLanguage: string
  packages: FormPackage[]
  trialUsed: boolean
  followedGamesMonthCount: string
  maxSeats: string
  billingContact: { name: string; phone: string; email: string }
}

export const toOrganizationFormValues = (organization?: InternalOrganization): OrganizationFormValues => ({
  ...(Object.fromEntries(editableTextFields.map((field) => [field, organization?.[field] ?? ''])) as Record<EditableTextField, string>),
  name: organization?.name ?? '',
  defaultLanguage: organization?.defaultLanguage ?? '',
  packages: organization?.packages ?? [],
  trialUsed: organization?.trialUsed ?? false,
  followedGamesMonthCount: String(organization?.followedGamesMonthCount ?? 0),
  maxSeats: String(organization?.maxSeats ?? 0),
  billingContact: {
    name: organization?.billingContact?.name ?? '',
    phone: organization?.billingContact?.phone ?? '',
    email: organization?.billingContact?.email ?? '',
  },
})

const formPackageKey = (planPackage: FormPackage) =>
  JSON.stringify([planPackage.packageId, planPackage.purchasedAt, planPackage.expiration ?? 0, planPackage.note ?? null])

/** Whether the dialog differs from what it opened with; typing a value back counts as unchanged. */
export const isOrganizationFormChanged = (values: OrganizationFormValues, initial: OrganizationFormValues) =>
  editableTextFields.some((field) => values[field] !== initial[field]) ||
  values.name !== initial.name ||
  values.defaultLanguage !== initial.defaultLanguage ||
  values.trialUsed !== initial.trialUsed ||
  values.followedGamesMonthCount !== initial.followedGamesMonthCount ||
  values.maxSeats !== initial.maxSeats ||
  values.billingContact.name !== initial.billingContact.name ||
  values.billingContact.phone !== initial.billingContact.phone ||
  values.billingContact.email !== initial.billingContact.email ||
  values.packages.map(formPackageKey).join('|') !== initial.packages.map(formPackageKey).join('|')

/** The backend stores whole numbers only. */
export const parseWholeNumber = (value: string) => (/^\d+$/.test(value.trim()) ? Number(value.trim()) : undefined)

export type WholeNumberError = 'required' | 'invalid' | 'too_large'

export const getWholeNumberError = (value: string, max?: number): WholeNumberError | undefined => {
  if (!value.trim()) return 'required'
  const number = parseWholeNumber(value)
  if (number === undefined) return 'invalid'
  return max !== undefined && max > 0 && number > max ? 'too_large' : undefined
}

/** Angular trims what is typed; an untouched value (equal to what the dialog `opened` with) is sent as `stored`, and anything empty as `''`. */
const toText = (value: string, stored?: string | null, opened: string | null | undefined = stored) => (value === (opened ?? '') ? stored || '' : value.trim())

const withoutId = (planPackage: FormPackage): SentOrganizationPackage => {
  if (!('id' in planPackage)) return planPackage
  const { id: _id, ...sent } = planPackage
  return sent
}

const toBillingContact = (
  values: OrganizationFormValues['billingContact'],
  stored?: OrganizationBillingContact | null,
  opened: OrganizationBillingContact | null | undefined = stored
): OrganizationBillingContact => {
  const toContactText = (field: keyof OrganizationBillingContact) =>
    values[field] === (opened?.[field] ?? '') ? (stored ? stored[field] : '') : values[field].trim()
  if (!stored && !opened) return { name: values.name.trim(), phone: values.phone.trim(), email: values.email.trim() }
  return { ...stored, name: toContactText('name'), phone: toContactText('phone'), email: toContactText('email') }
}

const toTexts = (values: OrganizationFormValues, stored?: InternalOrganization, opened = stored) =>
  Object.fromEntries(
    organizationTextFields.map((field) => {
      const editable = (editableTextFields as readonly string[]).includes(field)
      return [field, editable ? toText(values[field as EditableTextField], stored?.[field], opened?.[field]) : stored?.[field] || '']
    })
  ) as Record<OrganizationTextField, string>

/**
 * Legacy's update body: the stored organization with the dialog's fields applied. `stored` should be read right before saving and
 * `opened` is what the dialog was opened with, so only the fields changed in the dialog replace the stored ones and the rest
 * (e.g. `followedGames`, changed meanwhile by users) are sent as stored.
 */
export const toOrganizationUpdate = (
  stored: InternalOrganization,
  values: OrganizationFormValues,
  opened: InternalOrganization = stored
): OrganizationUpdate => {
  const initial = toOrganizationFormValues(opened)
  const changed = (field: 'trialUsed' | 'followedGamesMonthCount' | 'maxSeats') => values[field] !== initial[field]
  const packagesChanged = values.packages.map(formPackageKey).join('|') !== initial.packages.map(formPackageKey).join('|')
  return {
    ...stored,
    name: values.name === opened.name ? stored.name : values.name.trim(),
    defaultLanguage: values.defaultLanguage === initial.defaultLanguage ? stored.defaultLanguage : values.defaultLanguage,
    accountExpiresAt: stored.accountExpiresAt || (stored.accountExpiresAt === 0 ? 0 : ''),
    trialExpiresAt: stored.trialExpiresAt || (stored.trialExpiresAt === 0 ? 0 : ''),
    ...toTexts(values, stored, opened),
    billingContact: toBillingContact(values.billingContact, stored.billingContact, opened.billingContact),
    trialUsed: changed('trialUsed') ? values.trialUsed : stored.trialUsed,
    followedGamesMonthCount:
      (changed('followedGamesMonthCount') ? parseWholeNumber(values.followedGamesMonthCount) : undefined) ?? stored.followedGamesMonthCount,
    maxSeats: (changed('maxSeats') ? parseWholeNumber(values.maxSeats) : undefined) ?? stored.maxSeats,
    packages: packagesChanged ? values.packages.map(withoutId) : stored.packages.map(withoutId),
  }
}

/** Legacy's create body. Limits left at their defaults are not sent, like legacy's untouched inputs; the backend stores 0 and `false`. */
export const toOrganizationCreate = (values: OrganizationFormValues): OrganizationCreate => {
  const followedGamesMonthCount = parseWholeNumber(values.followedGamesMonthCount) ?? 0
  const maxSeats = parseWholeNumber(values.maxSeats) ?? 0
  return {
    credits: 0,
    creditsPerMonth: 0,
    packages: values.packages.map(withoutId),
    name: values.name.trim(),
    defaultLanguage: values.defaultLanguage,
    accountExpiresAt: '',
    trialExpiresAt: '',
    ...toTexts(values),
    billingContact: toBillingContact(values.billingContact),
    ...(values.trialUsed ? { trialUsed: true } : {}),
    ...(followedGamesMonthCount ? { followedGamesMonthCount } : {}),
    ...(maxSeats ? { maxSeats } : {}),
  }
}

export type AddPackageDates = {
  purchasedDate: Date
  purchasedTime: string
  expirationDate?: Date | null
  expirationTime?: string
  note: string
}

/** A local date at the `HH:mm` time of a time input. */
export const atTime = (date: Date, time: string) => {
  const [hours, minutes] = time.split(':').map(Number)
  return set(date, { hours, minutes, seconds: 0, milliseconds: 0 }).getTime()
}

export const isTime = (time?: string) => !!time && /^([01]\d|2[0-3]):[0-5]\d$/.test(time)

/** Legacy's added copies: the catalog package without `id`, with `packageId`, `purchasedAt`, and `expiration` and `note` only when given. */
export const toAddedPackages = (catalog: InternalPackage[], selectedIds: string[], dates: AddPackageDates): AddedOrganizationPackage[] => {
  const note = dates.note.trim()
  return catalog
    .filter(({ id }) => selectedIds.includes(id))
    .map(({ id, ...catalogPackage }) => ({
      ...catalogPackage,
      packageId: id,
      purchasedAt: atTime(dates.purchasedDate, dates.purchasedTime),
      ...(dates.expirationDate && dates.expirationTime ? { expiration: atTime(dates.expirationDate, dates.expirationTime) } : {}),
      ...(note ? { note } : {}),
    }))
}

export type OrganizationField =
  | 'name'
  | 'defaultLanguage'
  | 'packages'
  | 'trialUsed'
  | 'followedGamesMonthCount'
  | 'maxSeats'
  | 'billingContact'
  | EditableTextField

export const organizationFieldLabelKeys: Record<OrganizationField, string> = {
  name: 'internal-csm:organizations_company_name',
  defaultLanguage: 'internal-csm:organizations_default_language',
  packages: 'internal-csm:organizations_plans',
  trialUsed: 'internal-csm:organizations_trial_used',
  followedGamesMonthCount: 'internal-csm:organizations_followed_games_month_count',
  maxSeats: 'internal-csm:organizations_max_seats',
  billingContact: 'internal-csm:organizations_billing_contact',
  vatNumber: 'internal-csm:organizations_vat_number',
  billingType: 'internal-csm:organizations_invoice_type',
  invoiceCurrency: 'internal-csm:organizations_invoice_currency',
  invoiceFrequency: 'internal-csm:organizations_invoice_frequency',
  paymentTerms: 'internal-csm:organizations_payment_terms',
  billingAddress1: 'internal-csm:organizations_billing_address',
  billingAddress2: 'internal-csm:organizations_billing_address',
  billingZipCode: 'internal-csm:organizations_billing_zip_code',
  billingCity: 'internal-csm:organizations_billing_city',
  billingCountry: 'internal-csm:organizations_billing_country',
  address1: 'internal-csm:organizations_company_address',
  address2: 'internal-csm:organizations_company_address',
  zipCode: 'internal-csm:organizations_zip_code',
  city: 'internal-csm:organizations_city',
  country: 'internal-csm:organizations_country',
  contactName: 'internal-csm:organizations_contact_name',
  contactEmail: 'internal-csm:organizations_contact_email',
  contactPhone: 'internal-csm:organizations_contact_phone',
}

const sameText = (a?: string | null, b?: string | null) => (a ?? '') === (b ?? '')

const packageKey = (planPackage: SentOrganizationPackage | OrganizationPackage) =>
  JSON.stringify([planPackage.packageId, planPackage.purchasedAt, planPackage.expiration ?? 0, planPackage.note ?? ''])

const samePackages = (sent: SentOrganizationPackage[], stored: OrganizationPackage[]) => {
  const storedKeys = stored.map(packageKey).sort()
  const sentKeys = sent.map(packageKey).sort()
  return storedKeys.length === sentKeys.length && storedKeys.every((key, index) => key === sentKeys[index])
}

/** The dialog's fields the backend didn't store as sent. The limits it derives from the packages and the audit fields are not compared. */
export const getUnstoredOrganizationFields = (sent: OrganizationSave, stored: InternalOrganization): OrganizationField[] => {
  const fields: OrganizationField[] = []
  if (!sameText(sent.name, stored.name)) fields.push('name')
  if (!sameText(sent.defaultLanguage, stored.defaultLanguage)) fields.push('defaultLanguage')
  if (!samePackages(sent.packages, stored.packages)) fields.push('packages')
  if (!!sent.trialUsed !== !!stored.trialUsed) fields.push('trialUsed')
  if ((sent.followedGamesMonthCount ?? 0) !== stored.followedGamesMonthCount) fields.push('followedGamesMonthCount')
  if ((sent.maxSeats ?? 0) !== stored.maxSeats) fields.push('maxSeats')
  const contact = stored.billingContact
  if (
    !sameText(sent.billingContact.name, contact?.name) ||
    !sameText(sent.billingContact.phone, contact?.phone) ||
    !sameText(sent.billingContact.email, contact?.email)
  )
    fields.push('billingContact')
  editableTextFields.forEach((field) => {
    if (!sameText(sent[field], stored[field])) fields.push(field)
  })
  return fields
}
